import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ElementRef } from '@angular/core';

import * as Services from '@shared/core/services';
import { Controllers } from '@shared/core/controllers';
import { WizzardCollapseService } from '../../wizzard-collapse.service';

import { Observable, Subscription, of } from 'rxjs';
import { filter, skip, combineLatest, map, auditTime, withLatestFrom } from 'rxjs/operators';
import { IMenuFlowPage } from '@shared/state';

@Component({
    selector: 'shared-wizzard-collapse-page',
    templateUrl: './wizzard-collapse-page.shared.component.html',
    styleUrls: ['./wizzard-collapse-page.shared.component.scss'],
})
export class WizzardCollapsePageComponent implements OnInit, OnDestroy {
    public isExpanded: boolean = false;
    public hasCollapsed: boolean = false;
    public isOptional: boolean = false;

    @Input() public headerPaddingTop: OLO.Components.MARGIN_SIZE = 'lg';
    @Input() public headerPaddingBottom: OLO.Components.MARGIN_SIZE = 'lg';
    @Input() public menuFlowPage: IMenuFlowPage;
    @Input() public index: number;
    @Input() public isFixedOpen: boolean = false;
    @Input() public badgeSize: OLO.Components.BUTTON_SIZE = 'sm';

    public isComplete$: Observable<boolean>;
    public titlePrefix$: Observable<string>;
    public title$: Observable<string>;
    public hasErrors$: Observable<boolean>;
    public isEditing$: Observable<boolean>;
    public hasSomeProductsSelected$: Observable<boolean>; /* Cover default activated products */
    public isQuantityMaxedOut$: Observable<boolean>; /* Cover case when page should be expanded when multiple products can be selected */

    public pageChangeListener$: Subscription;
    public collapseListener$: Subscription;
    public errorsListener$: Subscription;

    constructor(
        public controllers: Controllers,
        public wizzardService: Services.WizzardService,
        public wizzardCollapseService: WizzardCollapseService,
        public changeDetectorRef: ChangeDetectorRef,
        public elementRef: ElementRef,
    ) { }

    ngOnInit(): void {
        if(this.elementRef) {
            this.wizzardCollapseService.registerPage(this.menuFlowPage.PageIdentifier, {
                element: this.elementRef.nativeElement,
                index: this.index,
                menuFlowPage: this.menuFlowPage,
            });
        }

        this.isExpanded = this.index === 0;
        this.isOptional = this.menuFlowPage && this.menuFlowPage.PageMinQuantity === 0;

        if (this.isExpanded) {
            this.wizzardCollapseService.setActivePageIndex(this.index);
        }

        this.isEditing$ = this.controllers.wizzard.isEditing$(true);
        this.isComplete$ = this.controllers.wizzard.isMenuFlowPageQuantityValid$(this.menuFlowPage);
        this.titlePrefix$ = this.controllers.wizzard.wizzardCollapsePageTitlePrefix$(this.menuFlowPage);
        this.title$ = this.controllers.wizzard.wizzardCollapsePageTitle$(this.menuFlowPage);
        this.hasErrors$ = this.controllers.wizzard.menuFlowPageHasErrors$(this.menuFlowPage.PageIdentifier);
        this.hasSomeProductsSelected$ = this.controllers.wizzard.pageHasSomeProductsSelected$(this.menuFlowPage);
        this.isQuantityMaxedOut$ = this.controllers.wizzard.isMenuFlowPageQuantityMaxedOut$(this.menuFlowPage);


        this.setupListeners();


    }

    ngOnDestroy(): void {
        this.killListeners();
    }

    public killListeners(): void {
        if (this.pageChangeListener$ && !this.pageChangeListener$.closed) this.pageChangeListener$.unsubscribe();
        if (this.collapseListener$ && !this.collapseListener$.closed) this.collapseListener$.unsubscribe();
        if (this.errorsListener$ && !this.errorsListener$.closed) this.errorsListener$.unsubscribe();

        this.wizzardCollapseService.reset();
    }

    public setupListeners(): void {
        //
        //  Wizzard tiles are 'smart' so in order to listen to any changes, we need to subscribe to state selectors and emit changes manually.
        //  It's a little MVVM pattern break, but there's no other easy and readable way around it.
        //  Then just handle flag in 'shouldCollapse' subscription..
        //  Just FYI - I HAVE NO FREAKING IDEA WHAT IS GOING ON HERE.. THOSE CONDITIONS... AND IT GETS BETTERN IN WIZZARDCOLLAPSESERVICE
        //

        /* #1 - listen to page changes and emit change info */
        this.pageChangeListener$ = this.controllers.wizzard.wizzardFirstProductForMenuFlowPage$(this.menuFlowPage.PageIdentifier)
            .subscribe((product) => {
                if (product) {
                    this.wizzardCollapseService.emitChange({ index: this.index, page: this.menuFlowPage });
                }
            });

        /* #2 - check if this page should collapse */
        this.collapseListener$ = this.wizzardCollapseService.shouldExpandCalculated$(this.menuFlowPage, this.index)
            .pipe(
                filter(expanded => expanded !== undefined && typeof expanded === 'boolean'),
                skip(1), /* Avoid expanded on init */
                combineLatest(
                    this.isQuantityMaxedOut$,
                ),
                auditTime(0), /* Too early closing */
            )
            .subscribe(([expand, isMaxedOut]) => {
                if (expand === false && isMaxedOut === false) return;
                this.setExpandedFlag(expand);
            });


        /* #3 - erorrs listener - make sure all fields gets expanded when trying to add to cart with error */
        this.errorsListener$ = this.hasErrors$
            .subscribe(hasErrors => {
                if (hasErrors) {
                    this.setExpandedFlag(true, true);
                }
            });

        /* #4 - next page open listener - hide current if is open && is complete */
        this.wizzardCollapseService.activePageIndex
            .pipe(
                combineLatest(
                    this.isComplete$,
                    this.isQuantityMaxedOut$,
                    this.wizzardCollapseService.activePageIndex,
                )
            )
            .subscribe(([nextPageIndex, isCurrentComplete, isMaxedOut, activeIndex]) => {
                if (nextPageIndex > this.index && this.isExpanded && (isCurrentComplete || isMaxedOut) && activeIndex !== this.index) {
                    this.setExpandedFlag(false);
                }
            });
    }


    public toggleCollapse(): void {
        this.setExpandedFlag(!this.isExpanded);
    }

    public setExpandedFlag(flag: boolean, ommitActive: boolean = false): void {
        if (flag && this.isExpanded || !flag && !this.isExpanded) return;

        if (!flag && this.isExpanded === true) {
            this.hasCollapsed = true;
        }

        this.isExpanded = flag;
        this.changeDetectorRef.detectChanges();

        if (flag && ommitActive === false) {
            this.wizzardCollapseService.setActivePageIndex(this.index);
        }

        this.wizzardCollapseService.stepChangeRegister(flag, this.menuFlowPage.PageIdentifier, this.index);
    }

    public get hasCollapsedFlag(): boolean {
        return this.hasCollapsed;
    }

    public get isPageValid$(): Observable<boolean> {
        return this.isComplete$
            .pipe(
                combineLatest(
                    this.isEditing$,
                    this.hasSomeProductsSelected$,
                    of(this.hasCollapsedFlag),
                ),
                map(([isComplete, isEditing, hasProductsSelected, hasCollapsed]) => isComplete && (hasCollapsed || isEditing) || hasProductsSelected),
            );
    }

    public get markAsComplete$(): Observable<boolean> {
        return this.isComplete$
            .pipe(
                combineLatest(
                    this.isEditing$,
                    this.hasSomeProductsSelected$,
                    this.hasErrors$,
                    of(this.hasCollapsedFlag),
                ),
                map(([isComplete, isEditing, hasProductsSelected, hasErrors, hasCollapsed]) => isComplete && !hasErrors && (hasCollapsed || isEditing || hasProductsSelected)),
            );
    }

    public get headerPrefixTitle$(): Observable<string> {
        return this.titlePrefix$
            .pipe(
                combineLatest(
                    this.hasSomeProductsSelected$
                ),
                map(([titlePrefix, hasSomeProductsSelected]) => titlePrefix && hasSomeProductsSelected ? titlePrefix : ''),
            );
    }

    public get headerSuffixTitle$(): Observable<string> {
        /* (isComplete$ | async) && (hasCollapsed === true || (isEditing$ | async)) ? (title$ | async) : '' */
        return this.titlePrefix$
            .pipe(
                combineLatest(
                    this.title$,
                    this.isEditing$,
                    this.isComplete$,
                    of(this.hasCollapsedFlag),
                ),
                map(([titlePrefix, title, isEditing, isComplete, hasCollapsed]) => title
                ),
            );
    }
}
